import React, { memo, useState } from 'react';
import { Add } from '@sharefiledev/icons';
import { Button, Flex, Form, Modal, theme, Typography } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { Component, PreparePageModalMaxHeight } from '../../../common/componentUtils';
import { useFetchDatasources } from '../../../common/hooks/useFetchDatasources';
import { useAppStore } from '../../../common/store';
import { VariableFieldEmptyState } from '../../../components/VariableFieldEmptyState';
import { handleError } from '../../../data/errorHandler';
import { PatchPayloadItem } from '../../../data/interfaces/APIRequest';
import { DocumentTemplate, Variable } from '../../../data/interfaces/DocumentTemplate';
import { InputValidation } from '../../../data/interfaces/form';
import { saveVariables, updateFieldsOfVariable } from '../../../data/variablesDataApi';
import { t } from '../../../utils';
import { isUniqueName, validateName, validateVariables } from '../../../utils/validator';
import { VariableItem } from './VariableItem';

const { Text } = Typography;

const getEmptyVariable = (): Variable & { isCreatedNew: boolean } => ({
	id: uuidv4(),
	name: '',
	provider: '',
	entity: '',
	entityId: '',
	connectionRID: '',
	version: '',
	isCreatedNew: true,
});

const useVariables = (documentTemplate: DocumentTemplate, updateDocumentTemplate) => {
	const variables: Variable[] = documentTemplate?.variables;

	// eslint-disable-next-line @typescript-eslint/no-shadow
	const setVariables = (variables: Variable[]) => {
		updateDocumentTemplate({ variables });
	};

	return { variables, setVariables };
};

type VariableProps = {
	isShownInModal?: boolean;
	onSave?: (variable: Variable) => void;
};

export const AddVariables = memo(({ isShownInModal, onSave }: VariableProps) => {
	const { token } = theme.useToken();

	const { documentTemplate, updateDocumentTemplate } = useAppStore(state => ({
		documentTemplate: state.documentTemplate,
		updateDocumentTemplate: state.updateDocumentTemplate,
	}));

	const { isDatasourcesFetched, dataSources } = useFetchDatasources();
	const hasNoThirdPartyIntegrationEnabled =
		isDatasourcesFetched && dataSources.length === 0;
	const [localVariables, setLocalVariables] = useState(documentTemplate?.variables);
	const [variablesToDelete, setVariablesToDelete] = useState<string[]>([]);
	const { variables, setVariables } = useVariables(
		documentTemplate,
		updateDocumentTemplate
	);

	const [modal, contextHolder] = Modal.useModal();
	const items: Variable[] = isShownInModal ? localVariables : variables;

	const updateVariables = (vars: Variable[]) => {
		if (isShownInModal) {
			setLocalVariables(vars);
		} else {
			setVariables(vars);
		}
	};

	const handleAddVariable = () => {
		if (items?.length) {
			updateVariables([...items, getEmptyVariable()]);
		} else {
			updateVariables([getEmptyVariable()]);
		}
	};

	const onUpdate = (obj: Partial<Variable>, id: string) => {
		if (obj.name) {
			(obj as any).isCreatedNew = false;
		}

		updateVariables(
			items.map(variable => (variable.id === id ? { ...variable, ...obj } : variable))
		);
	};

	const handleSaveVariables = () => {
		const errorMessage = validateVariables(items);

		if (errorMessage !== '') {
			handleError(errorMessage);
		} else {
			setVariables(items);

			if (isShownInModal) {
				const deletedVariableIds =
					documentTemplate?.variables
						?.filter(
							originalVariable =>
								!items.some(localVariable => localVariable.id === originalVariable.id)
						)
						?.map(variable => variable.id) || [];

				if (deletedVariableIds.length > 0) {
					const updatedFields =
						documentTemplate?.fields?.filter(
							(field: Component) => !deletedVariableIds.includes(field.variableId)
						) || [];

					updateDocumentTemplate({ fields: updatedFields });
				}
			}

			if (items) {
				onSave?.(items?.[0]);
				saveData(items);
			}
		}
	};

	const saveData = async (itms: Variable[]) => {
		const payload: PatchPayloadItem[] = [
			{
				op: 'replace',
				path: '/variables',
				value: itms,
			},
		];

		if (documentTemplate?.fields && variablesToDelete.length > 0) {
			const updatedFields = documentTemplate.fields.filter(
				(field: Component) => !variablesToDelete.includes(field.variableId)
			);

			payload.push({
				op: 'replace',
				path: '/fields',
				value: updateFieldsOfVariable(updatedFields, itms),
			});
		} else if (documentTemplate?.fields) {
			payload.push({
				op: 'replace',
				path: '/fields',
				value: updateFieldsOfVariable(documentTemplate?.fields, itms),
			});
		}

		const patchResponse = await saveVariables(payload, documentTemplate.rid);
		updateDocumentTemplate(patchResponse);

		setVariablesToDelete([]);
	};

	const handleDeleteVariable = (id: string) => {
		if (documentTemplate?.fields) {
			const variableWithFields = documentTemplate.fields.find(
				field => field.variableId === id
			);
			if (variableWithFields) {
				const deleteVariableWithFieldModalConfig = {
					title: t('variables.fieldsAssignedWarningHeader'),
					content: <Text>{t('variables.fieldsAssignedWarningMessage')}</Text>,
					okText: t('confirm'),
				};

				const handleWarningForVariableRemoval = async (confirmDeleteCall: () => void) => {
					const confirmed = await modal.confirm(deleteVariableWithFieldModalConfig);
					if (confirmed) {
						confirmDeleteCall();
					}
				};

				handleWarningForVariableRemoval(() => {
					deleteVariable(id);
					if (isShownInModal) {
						setVariablesToDelete(prev => [...prev, id]);
					} else {
						const updatedFields = documentTemplate?.fields?.filter(
							(field: Component) => field.variableId !== id
						);
						updateDocumentTemplate({ fields: updatedFields });
					}
				});
			} else {
				deleteVariable(id);
			}
		} else {
			deleteVariable(id);
		}
	};

	const deleteVariable = (id: string) => {
		const updatedVariables = items?.filter(v => v.id !== id);
		updateVariables(updatedVariables);
	};

	const modalStyles = isShownInModal
		? {
				maxHeight: PreparePageModalMaxHeight,
				overflow: 'auto',
				marginBlockEnd: token.marginLG,
				paddingLeft: token.paddingSM,
		  }
		: {
				marginBlockEnd: token.marginLG,
		  };

	const validateVariable = (variable: Variable) => {
		const isNewlyAddedVariable = (variable as any).isCreatedNew;

		let result: InputValidation = {
			status: 'success',
			message: '',
		};
		if (isNewlyAddedVariable) {
			return result;
		}

		result = validateName(variable.name, t('variableNameValidationError'));
		if (result.status === 'success') {
			if (!isUniqueName(variable.name, items)) {
				result = {
					status: 'error',
					message: t('variables.errorMessages.uniqueName'),
				};
			}
		}

		return result;
	};

	return (
		<Flex vertical>
			{items && items.length > 0 && (
				<Flex vertical style={modalStyles}>
					<Form layout="vertical">
						{items.map((variable, index) => {
							return (
								<VariableItem
									id={index + 1}
									key={variable.id}
									variable={variable}
									onRemove={handleDeleteVariable}
									onUpdate={onUpdate}
									dataSources={dataSources}
									validationState={validateVariable(variable)}
									isShownInModal={isShownInModal}
								/>
							);
						})}
					</Form>
				</Flex>
			)}
			{hasNoThirdPartyIntegrationEnabled && <VariableFieldEmptyState />}
			{contextHolder}
			<Flex justify="space-between">
				<Button
					type={hasNoThirdPartyIntegrationEnabled ? 'default' : 'primary'}
					icon={<Add size={16} />}
					onClick={() => handleAddVariable()}
					disabled={
						hasNoThirdPartyIntegrationEnabled || !documentTemplate?.file?.fileName
					}
				>
					{variables?.length
						? t('variables.addAnotherVariable')
						: t('variables.addNewVariable')}
				</Button>
				{isShownInModal && (
					<Button onClick={() => handleSaveVariables()}>{t('save')}</Button>
				)}
			</Flex>
		</Flex>
	);
});
